import React, { useState, useEffect } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  FaRocket,
  FaCheckCircle,
  FaBuilding,
  FaUser,
  FaIndustry,
  FaWhatsapp,
} from "react-icons/fa";
import { COLORS } from "../Utiles/Constants";
import { useCreateEnquiryMutation } from "../redux/api/Enquiry/EnquiryApi";
import { toast } from "react-toastify";

const EnquiryModal = ({
  autoOpen = true,
  autoOpenDelay = 2000,
  showButton = false,
  buttonText = "Get Demo",
  buttonClassName = "enquiry-button",
  modalTitle = "Get Started with WhatsApp Automation",
  modalClassName = "enquiry-modal",
  successTimeout = 3000,
  showMessageField = true,
  show: controlledShow,
  onHide: controlledOnHide,
}) => {
  const [internalShow, setInternalShow] = useState(false);
  const [autoOpenTriggered, setAutoOpenTriggered] = useState(false);
  const [success, setSuccess] = useState(false);

  const [createEnquiry, { isLoading }] = useCreateEnquiryMutation();

  const industryOptions = [
    { _id: "1", value: "E-commerce", icon: "🛒" },
    { _id: "2", value: "Healthcare", icon: "🏥" },
    { _id: "3", value: "Education", icon: "🎓" },
    { _id: "4", value: "Travel", icon: "✈️" },
    { _id: "5", value: "Real Estate", icon: "🏠" },
    { _id: "6", value: "Food & Restaurant", icon: "🍕" },
    { _id: "7", value: "Other", icon: "🔧" },
  ];

  const SESSION_STORAGE_KEY = "enquiryModalDismissed";

  const show = controlledShow !== undefined ? controlledShow : internalShow;

  useEffect(() => {
    if (autoOpen && !autoOpenTriggered && controlledShow === undefined) {
      const modalDismissed = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!modalDismissed) {
        const timer = setTimeout(() => {
          setInternalShow(true);
          setAutoOpenTriggered(true);
        }, autoOpenDelay);
        return () => clearTimeout(timer);
      } else {
        setAutoOpenTriggered(true);
      }
    }
  }, [autoOpen, autoOpenDelay, autoOpenTriggered, controlledShow]);

  const handleClose = () => {
    if (controlledOnHide) {
      controlledOnHide();
    } else {
      setInternalShow(false);
      sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
    }
    setSuccess(false);
  };

  const handleOpen = () => {
    if (controlledShow === undefined) {
      setInternalShow(true);
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  };

  const getInitialValues = () => {
    const initialValues = { name: "", phone: "", company: "", industry: "" };
    if (showMessageField) initialValues.message = "";
    return initialValues;
  };

  const getValidationSchema = () =>
    Yup.object().shape({
      name: Yup.string().required("Your name is required"),
      company: Yup.string().required("Company name is required"),
      phone: Yup.string()
        .required("Phone number is required")
        .matches(/^[6-9][0-9]{9}$/, "Enter a valid mobile number"),
      industry: Yup.string().required("Select your industry"),
      ...(showMessageField && { message: Yup.string().nullable() }),
    });

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const response = await createEnquiry(values).unwrap();

      if (response.success) {
        setSuccess(true);
        resetForm();

        toast.success("Enquiry submitted successfully!", {
          autoClose: 2000,
          position: "bottom-center",
        });

        setTimeout(() => {
          handleClose();
          setSuccess(false);
        }, successTimeout);
      }
    } catch (err) {
      console.error("Enquiry submission error:", err);
      toast.error(
        err?.data?.message || "Something went wrong. Please try again.",
        {
          autoClose: 3000,
          position: "bottom-center",
        }
      );
    }
  };

  const customStyles = {
    modal: {
      borderRadius: "16px",
      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
      border: "none",
      overflow: "hidden",
    },
    modalHeader: {
      background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
      color: "white",
      padding: "20px 25px 15px",
      borderBottom: "none",
      position: "relative",
    },
    modalBody: {
      padding: "25px",
      background: COLORS.light,
    },
    button: {
      background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
      border: "none",
      borderRadius: "12px",
      padding: "12px 24px",
      fontWeight: "600",
      fontSize: "16px",
      color: "white",
      boxShadow: `0 4px 15px ${COLORS.primary}40`,
      transition: "all 0.3s ease",
      cursor: "pointer",
    },
    formControl: {
      borderRadius: "10px",
      border: `2px solid ${COLORS.light}`,
      padding: "12px 15px",
      fontSize: "15px",
      transition: "all 0.3s ease",
      backgroundColor: "white",
    },
    successIcon: {
      color: COLORS.success,
      fontSize: "4rem",
      animation: "bounceIn 0.6s ease-out",
    },
  };

  return (
    <>
      {showButton && (
        <button
          onClick={handleOpen}
          className={`${buttonClassName}`}
          style={{
            ...customStyles.button,
            transform: "translateY(0)",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = `0 6px 20px ${COLORS.primary}50`;
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = `0 4px 15px ${COLORS.primary}40`;
          }}
        >
          <FaRocket className="me-2" />
          {buttonText}
        </button>
      )}

      <Modal
        show={show}
        onHide={handleClose}
        centered
        backdrop="static"
        className={modalClassName}
        size="md"
      >
        <div style={customStyles.modal}>
          <Modal.Header
            closeButton
            style={customStyles.modalHeader}
            className="text-center"
          >
            <div className="w-100">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FaWhatsapp className="me-3" style={{ fontSize: "2rem" }} />
                <h3 className="mb-0 fw-bold">{modalTitle}</h3>
                <FaRocket className="ms-3" style={{ fontSize: "1.5rem" }} />
              </div>
              <p className="mb-0 opacity-90">
                Transform your business communication today!
              </p>
            </div>
          </Modal.Header>

          <Modal.Body style={customStyles.modalBody}>
            <div className="text-center mb-4">
              <p
                className="text-muted mb-3"
                style={{ fontSize: "15px", lineHeight: "1.6" }}
              >
                Join <strong style={{ color: COLORS.primary }}>1000+</strong>{" "}
                businesses using our
                <strong className="mx-1" style={{ color: COLORS.secondary }}>
                  WhatsApp automation, chatbots, and campaigns
                </strong>
                to boost sales & engagement.
              </p>
            </div>

            {success ? (
              <div className="text-center py-4">
                <div className="mb-4">
                  <FaCheckCircle style={customStyles.successIcon} />
                </div>
                <h4
                  style={{
                    color: COLORS.dark,
                    fontWeight: "700",
                    marginBottom: "15px",
                  }}
                >
                  🎉 Thanks for your interest!
                </h4>
                <p
                  style={{
                    color: COLORS.dark,
                    fontSize: "16px",
                    lineHeight: "1.6",
                  }}
                >
                  Our WhatsApp automation experts will reach out within
                  <strong style={{ color: COLORS.primary }}>
                    {" "}
                    30 minutes
                  </strong>{" "}
                  to schedule your personalized demo.
                </p>
                <div
                  style={{
                    background: `${COLORS.success}10`,
                    padding: "15px",
                    borderRadius: "10px",
                    border: `1px solid ${COLORS.success}30`,
                    marginTop: "20px",
                  }}
                >
                  <small style={{ color: COLORS.success, fontWeight: "600" }}>
                    ✨ Get ready to see 3X faster customer responses!
                  </small>
                </div>
              </div>
            ) : (
              <Formik
                initialValues={getInitialValues()}
                validationSchema={getValidationSchema()}
                onSubmit={handleSubmit}
              >
                {({
                  handleSubmit,
                  handleChange,
                  values,
                  touched,
                  errors,
                  setFieldTouched,
                }) => (
                  <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <div className="input-group-custom">
                        <div className="input-icon">
                          <FaUser style={{ color: COLORS.primary }} />
                        </div>
                        <Form.Control
                          type="text"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={() => setFieldTouched("name", true)}
                          placeholder="Your Full Name"
                          isInvalid={touched.name && !!errors.name}
                          style={{
                            ...customStyles.formControl,
                            paddingLeft: "45px",
                            ...(touched.name && !errors.name && values.name
                              ? {
                                  borderColor: COLORS.success,
                                  backgroundColor: `${COLORS.success}05`,
                                }
                              : {}),
                          }}
                          required
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <div className="input-group-custom">
                        <div className="input-icon">
                          <FaBuilding style={{ color: COLORS.secondary }} />
                        </div>
                        <Form.Control
                          type="text"
                          name="company"
                          value={values.company}
                          onChange={handleChange}
                          onBlur={() => setFieldTouched("company", true)}
                          placeholder="Company / Business Name"
                          isInvalid={touched.company && !!errors.company}
                          style={{
                            ...customStyles.formControl,
                            paddingLeft: "45px",
                            ...(touched.company &&
                            !errors.company &&
                            values.company
                              ? {
                                  borderColor: COLORS.success,
                                  backgroundColor: `${COLORS.success}05`,
                                }
                              : {}),
                          }}
                          required
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.company}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <div className="input-group-custom">
                        <div className="input-icon">
                          <FaWhatsapp style={{ color: COLORS.primary }} />
                        </div>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={values.phone}
                          onChange={handleChange}
                          onBlur={() => setFieldTouched("phone", true)}
                          placeholder="WhatsApp Number (10 digits)"
                          isInvalid={touched.phone && !!errors.phone}
                          style={{
                            ...customStyles.formControl,
                            paddingLeft: "45px",
                            ...(touched.phone && !errors.phone && values.phone
                              ? {
                                  borderColor: COLORS.success,
                                  backgroundColor: `${COLORS.success}05`,
                                }
                              : {}),
                          }}
                          required
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.phone}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <div className="input-group-custom">
                        <div className="input-icon">
                          <FaIndustry style={{ color: COLORS.accent }} />
                        </div>
                        <Form.Control
                          as="select"
                          name="industry"
                          value={values.industry}
                          onChange={handleChange}
                          onBlur={() => setFieldTouched("industry", true)}
                          isInvalid={touched.industry && !!errors.industry}
                          style={{
                            ...customStyles.formControl,
                            paddingLeft: "45px",
                            ...(touched.industry &&
                            !errors.industry &&
                            values.industry
                              ? {
                                  borderColor: COLORS.success,
                                  backgroundColor: `${COLORS.success}05`,
                                }
                              : {}),
                          }}
                          required
                        >
                          <option value="" disabled>
                            Choose Your Industry
                          </option>
                          {industryOptions.map((opt) => (
                            <option key={opt._id} value={opt.value}>
                              {opt.icon} {opt.value}
                            </option>
                          ))}
                        </Form.Control>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.industry}
                      </Form.Control.Feedback>
                    </Form.Group>

                    {showMessageField && (
                      <Form.Group className="mb-4">
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="message"
                          value={values.message}
                          onChange={handleChange}
                          placeholder="Tell us your specific needs... (e.g., customer support chatbot, marketing campaigns, appointment booking)"
                          isInvalid={touched.message && !!errors.message}
                          style={{
                            ...customStyles.formControl,
                            resize: "vertical",
                            minHeight: "80px",
                          }}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    )}

                    <div className="d-grid gap-2 mb-4">
                      <button
                        type="submit"
                        disabled={isLoading}
                        style={{
                          ...customStyles.button,
                          padding: "15px",
                          fontSize: "17px",
                          fontWeight: "700",
                        }}
                      >
                        {isLoading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                            <span className="ms-2">
                              Setting up your demo...
                            </span>
                          </>
                        ) : (
                          <>
                            <FaRocket className="me-2" />
                            Get My FREE Demo Now!
                          </>
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </Modal.Body>
        </div>

        <style>{`
          .input-group-custom {
            position: relative;
          }

          .input-icon {
            position: absolute;
            left: 15px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 10;
          }

          @keyframes bounceIn {
            0% {
              transform: scale(0);
              opacity: 0;
            }
            50% {
              transform: scale(1.1);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }

          .modal-backdrop {
            background-color: rgba(22, 30, 45, 0.8) !important;
          }
        `}</style>
      </Modal>
    </>
  );
};

export default EnquiryModal;